'use client'
import { useState, useRef, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  GripVertical,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  ListOrdered,
  ChevronUp,
  ChevronDown,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'

interface Network {
  id: string;
  name: string;
  health?: number;
  status?: string;
  uplink_health?: number;
  wireless_health?: number;
  switch_health?: number;
  overall_health?: number;
}

interface NetworkHealthListProps {
  networks: Network[];
  allNetworks?: Network[];
  totalCount?: number;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

type SortMode = 'score-asc' | 'score-desc' | 'manual'

const STORAGE_KEY = 'noc-network-health-order'
const SORT_MODE_KEY = 'noc-network-health-sort-mode'

const calculateHealthScore = (network: Network): number => {
  if (network.health !== undefined && network.health !== null) {
    return Math.round(Math.max(0, Math.min(100, network.health)))
  }
  if (network.overall_health !== undefined && network.overall_health !== null) {
    return Math.round(Math.max(0, Math.min(100, network.overall_health)))
  }
  const healthScores: number[] = []
  if (network.uplink_health !== undefined && network.uplink_health !== null) healthScores.push(network.uplink_health)
  if (network.wireless_health !== undefined && network.wireless_health !== null) healthScores.push(network.wireless_health)
  if (network.switch_health !== undefined && network.switch_health !== null) healthScores.push(network.switch_health)
  if (healthScores.length > 0) {
    const avg = healthScores.reduce((sum, s) => sum + s, 0) / healthScores.length
    return Math.round(Math.max(0, Math.min(100, avg)))
  }
  if (network.status) {
    const s = network.status.toLowerCase()
    if (s === 'online' || s === 'active') return 95
    if (s === 'alerting' || s === 'warning') return 65
    if (s === 'offline' || s === 'down') return 25
    if (s === 'dormant') return 50
  }
  return 85
}

const getScoreColor = (score: number) => {
  if (score >= 80) return { bar: 'bg-[#8AA2DF]', bg: 'bg-[#DDE3FB]', badge: 'bg-[#DDE3FB] text-[#535EA4]' }
  if (score >= 60) return { bar: 'bg-[#8AA2DF]', bg: 'bg-[#DDE3FB]', badge: 'bg-[#DDE3FB] text-[#535EA4]' }
  if (score >= 40) return { bar: 'bg-[#EDBE90]', bg: 'bg-[#F5E6D3]', badge: 'bg-[#F5E6D3] text-[#C07820]' }
  return { bar: 'bg-[#CD585A]', bg: 'bg-[#F5D3D3]', badge: 'bg-[#F5D3D3] text-[#CD585A]' }
}

export function NetworkHealthList({
  networks,
  allNetworks,
  totalCount = 0,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
}: NetworkHealthListProps) {
  const { t } = useLanguage()
  const [sortMode, setSortMode] = useState<SortMode>(() => {
    try {
      const stored = localStorage.getItem(SORT_MODE_KEY)
      if (stored === 'manual' || stored === 'score-asc' || stored === 'score-desc') return stored
    } catch { /* ignore */ }
    return 'score-asc'
  })
  const [manualOrder, setManualOrder] = useState<string[]>([])
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const dragNodeRef = useRef<HTMLDivElement | null>(null)

  // Reorder modal state
  const [reorderOpen, setReorderOpen] = useState(false)
  const [modalOrder, setModalOrder] = useState<string[]>([])
  const [modalDragIndex, setModalDragIndex] = useState<number | null>(null)
  const [modalDragOverIndex, setModalDragOverIndex] = useState<number | null>(null)
  const modalDragNodeRef = useRef<HTMLDivElement | null>(null)

  // Initialise / sync manual order using all networks (across all pages) so
  // cross-page reordering works correctly.
  useEffect(() => {
    const allNets = (allNetworks && allNetworks.length > 0) ? allNetworks : networks
    if (allNets.length === 0) return
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed: string[] = JSON.parse(stored)
        const knownIds = new Set(allNets.map((n) => n.id))
        const merged = parsed.filter((id) => knownIds.has(id))
        allNets.forEach((n) => { if (!merged.includes(n.id)) merged.push(n.id) })
        setManualOrder(merged)
        return
      }
    } catch { /* ignore */ }
    setManualOrder(allNets.map((n) => n.id))
  }, [allNetworks, networks])

  const persistOrder = useCallback((order: string[]) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(order)) } catch { /* ignore */ }
  }, [])

  // Persist sort mode whenever it changes
  useEffect(() => {
    try { localStorage.setItem(SORT_MODE_KEY, sortMode) } catch { /* ignore */ }
  }, [sortMode])

  const getDisplayNetworks = (): Network[] => {
    if (sortMode === 'manual') {
      // In manual mode, use ALL networks (across all pages) so cross-page
      // reordering is respected. Paginate locally using the server page size.
      const map = new Map(sourceNetworks.map((n) => [n.id, n]))
      const ordered = manualOrder.map((id) => map.get(id)).filter(Boolean) as Network[]
      const ps = networks.length > 0 ? networks.length : 10
      const start = (currentPage - 1) * ps
      return ordered.slice(start, start + ps)
    }
    return [...networks].sort((a, b) => {
      const diff = calculateHealthScore(a) - calculateHealthScore(b)
      return sortMode === 'score-asc' ? diff : -diff
    })
  }

  const cycleSortMode = () => {
    setSortMode((prev) => {
      if (prev === 'score-asc') return 'score-desc'
      if (prev === 'score-desc') return 'manual'
      return 'score-asc'
    })
  }

  const resetOrder = () => {
    const defaultOrder = sourceNetworks.map((n) => n.id)
    setManualOrder(defaultOrder)
    persistOrder(defaultOrder)
  }

  // Main card drag handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDragIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    dragNodeRef.current = e.currentTarget
    setTimeout(() => { if (dragNodeRef.current) dragNodeRef.current.style.opacity = '0.4' }, 0)
  }

  const handleDragEnter = (index: number) => {
    setDragOverIndex(index)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault()
    if (dragIndex === null || dragIndex === dropIndex) return
    // Build the full ordered list across all pages and splice at absolute positions
    const map = new Map(sourceNetworks.map((n) => [n.id, n]))
    const fullOrdered = manualOrder.map((id) => map.get(id)).filter(Boolean) as Network[]
    const ps = networks.length > 0 ? networks.length : 10
    const pageStart = (currentPage - 1) * ps
    const absFrom = pageStart + dragIndex
    const absTo = pageStart + dropIndex
    const orderedIds = fullOrdered.map((n) => n.id)
    const [moved] = orderedIds.splice(absFrom, 1)
    orderedIds.splice(absTo, 0, moved)
    // Preserve any IDs that aren't in sourceNetworks (e.g. filtered/deleted networks)
    const sourceIds = new Set(sourceNetworks.map((n) => n.id))
    const extraIds = manualOrder.filter((id) => !sourceIds.has(id))
    const newOrder = [...orderedIds, ...extraIds]
    setManualOrder(newOrder)
    persistOrder(newOrder)
    setSortMode('manual')
    setDragIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    if (dragNodeRef.current) dragNodeRef.current.style.opacity = '1'
    dragNodeRef.current = null
    setDragIndex(null)
    setDragOverIndex(null)
  }

  // Reorder modal logic
  const sourceNetworks = (allNetworks && allNetworks.length > 0) ? allNetworks : networks

  const openReorderModal = () => {
    // Build ordered list of ALL networks respecting current manualOrder
    const allIds = sourceNetworks.map((n) => n.id)
    const storedOrder: string[] = (() => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) return JSON.parse(stored) as string[]
      } catch { /* ignore */ }
      return manualOrder
    })()
    // Merge: keep stored order positions for known ids, append remaining
    const known = new Set(allIds)
    const ordered = storedOrder.filter((id) => known.has(id))
    allIds.forEach((id) => { if (!ordered.includes(id)) ordered.push(id) })
    setModalOrder(ordered)
    setReorderOpen(true)
  }

  const closeReorderModal = () => {
    // Apply modal order back to manualOrder and persist
    setManualOrder(modalOrder)
    persistOrder(modalOrder)
    setSortMode('manual')
    setReorderOpen(false)
  }

  const getModalNetworks = (): Network[] => {
    const map = new Map(sourceNetworks.map((n) => [n.id, n]))
    return modalOrder.map((id) => map.get(id)).filter(Boolean) as Network[]
  }

  // Modal drag handlers
  const handleModalDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setModalDragIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    modalDragNodeRef.current = e.currentTarget
    setTimeout(() => { if (modalDragNodeRef.current) modalDragNodeRef.current.style.opacity = '0.4' }, 0)
  }

  const handleModalDragEnter = (index: number) => {
    setModalDragOverIndex(index)
  }

  const handleModalDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleModalDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault()
    if (modalDragIndex === null || modalDragIndex === dropIndex) return
    const newOrder = [...modalOrder]
    const [moved] = newOrder.splice(modalDragIndex, 1)
    newOrder.splice(dropIndex, 0, moved)
    setModalOrder(newOrder)
    setModalDragIndex(null)
    setModalDragOverIndex(null)
  }

  const handleModalDragEnd = () => {
    if (modalDragNodeRef.current) modalDragNodeRef.current.style.opacity = '1'
    modalDragNodeRef.current = null
    setModalDragIndex(null)
    setModalDragOverIndex(null)
  }

  const moveModalItem = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...modalOrder]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newOrder.length) return
    ;[newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]]
    setModalOrder(newOrder)
  }

  const displayNetworks = getDisplayNetworks()
  const effectivePageSize = networks.length > 0 ? networks.length : 10
  const effectiveTotalCount = sortMode === 'manual' ? sourceNetworks.length : totalCount
  const effectiveTotalPages = sortMode === 'manual' ? Math.ceil(sourceNetworks.length / effectivePageSize) : totalPages
  const startIndex = (currentPage - 1) * effectivePageSize + 1
  const endIndex = Math.min(startIndex + effectivePageSize - 1, effectiveTotalCount)

  const SortIcon = sortMode === 'score-asc' ? ArrowUp : sortMode === 'score-desc' ? ArrowDown : ArrowUpDown

  const modalNetworks = getModalNetworks()

  return (
    <>
      <Card className="border border-[#DDD] shadow-none bg-white rounded-[12px] h-[400px] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between pb-3 px-5 pt-5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <CardTitle className="text-[16px] font-medium text-[#676767]">{t.networkHealth.title}</CardTitle>
            <div className="relative group">
              <Sparkles className="h-4 w-4 text-[#535EA4]" />
              <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                AI-Powered Health Analysis
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* Reorder all button */}
            <button
              onClick={openReorderModal}
              title="Reorder all networks"
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-[#535EA4] hover:bg-[#F0F3FF] transition-colors"
            >
              <ListOrdered className="h-3.5 w-3.5" />
              <span>Reorder</span>
            </button>

            {/* Sort toggle */}
            <button
              onClick={cycleSortMode}
              title={
                sortMode === 'score-asc'
                  ? 'Sorted: lowest score first — click for highest first'
                  : sortMode === 'score-desc'
                  ? 'Sorted: highest score first — click for manual order'
                  : 'Manual order — click to sort by score'
              }
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-[#676767] hover:bg-[#F5F5F5] transition-colors"
            >
              <SortIcon className="h-3 w-3" />
              <span>
                {sortMode === 'score-asc' ? 'Low→High' : sortMode === 'score-desc' ? 'High→Low' : 'Manual'}
              </span>
            </button>

            {/* Reset order (only visible in manual mode) */}
            {sortMode === 'manual' && (
              <button
                onClick={resetOrder}
                title="Reset to default order"
                className="p-1 rounded-md text-[#939393] hover:text-[#535EA4] hover:bg-[#F5F5F5] transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </CardHeader>

        <CardContent className="px-5 pb-5 flex-1 overflow-auto">
          {/* Column headers */}
          {displayNetworks.length > 0 && (
            <div className="flex items-center gap-2 mb-2 px-1">
              <div className="w-4" />
              <span className="text-[10px] text-[#B0B0B0] w-[62px]">Network</span>
              <div className="flex-1" />
              <span className="text-[10px] text-[#B0B0B0] w-[28px] text-right">Score</span>
            </div>
          )}

          <div className="space-y-[10px]">
            {displayNetworks.length > 0 ? (
              displayNetworks.map((net, index) => {
                const score = calculateHealthScore(net)
                const colors = getScoreColor(score)
                const displayName = net.name.length > 12 ? net.name.substring(0, 12) + '…' : net.name
                const isDragging = dragIndex === index
                const isDragOver = dragOverIndex === index && dragIndex !== index

                return (
                  <div
                    key={net.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-2 h-[22px] group rounded-[4px] transition-all
                      ${isDragOver ? 'ring-2 ring-[#535EA4] ring-offset-1 bg-[#F0F3FF]' : ''}
                      ${isDragging ? 'opacity-40' : ''}
                    `}
                    title={`${net.name} — Health Score: ${score}/100`}
                  >
                    <GripVertical
                      className="h-3.5 w-3.5 text-[#D0D0D0] group-hover:text-[#939393] cursor-grab active:cursor-grabbing flex-shrink-0 transition-colors"
                    />
                    <span className="text-[12px] font-medium text-[#939393] w-[62px] truncate flex-shrink-0" title={net.name}>
                      {displayName}
                    </span>
                    <div className="flex-1 h-[14px] flex items-center min-w-0">
                      <div className={`h-[14px] rounded-[3px] ${colors.bg} w-full relative overflow-hidden`}>
                        <div
                          className={`h-full ${colors.bar} transition-all duration-500 ease-out rounded-[3px]`}
                          style={{ width: `${score}%` }}
                        />
                      </div>
                    </div>
                    <span
                      className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-[4px] w-[34px] text-center flex-shrink-0 tracking-tight ${colors.badge}`}
                    >
                      {score}
                    </span>
                  </div>
                )
              })
            ) : (
              <div className="flex items-center justify-center h-[200px] text-[#A4A4A4]">
                <p className="text-[14px]">{t.common.loading}</p>
              </div>
            )}
          </div>

          {displayNetworks.length > 0 && (
            <p className="text-[10px] text-[#C0C0C0] mt-3 text-center">
              {sortMode === 'manual' ? 'Drag rows to reorder · or use Reorder to arrange all networks' : 'Drag a row to reorder · or click Reorder to arrange all networks'}
            </p>
          )}
        </CardContent>

        {/* Pagination */}
        {effectiveTotalPages > 1 && (
          <div className="border-t border-[#DDD] px-5 py-3 flex items-center justify-between flex-shrink-0">
            <div className="text-[12px] text-[#676767]">
              Showing {startIndex}–{endIndex} of {effectiveTotalCount} networks
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange?.(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-[12px] text-[#676767] min-w-[80px] text-center">
                Page {currentPage} of {effectiveTotalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange?.(currentPage + 1)}
                disabled={currentPage === effectiveTotalPages}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Reorder All Networks Modal */}
      {reorderOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeReorderModal}
          />

          {/* Modal panel */}
          <div className="relative z-10 bg-white rounded-[16px] shadow-2xl w-[480px] max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#EEE] flex-shrink-0">
              <div>
                <h2 className="text-[15px] font-semibold text-[#333]">Reorder Networks</h2>
                <p className="text-[11px] text-[#999] mt-0.5">
                  Drag rows or use arrows to set the display order across all pages
                </p>
              </div>
              <button
                onClick={closeReorderModal}
                className="p-1.5 rounded-lg text-[#999] hover:text-[#333] hover:bg-[#F5F5F5] transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Scrollable list */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5">
              {modalNetworks.map((net, index) => {
                const score = calculateHealthScore(net)
                const colors = getScoreColor(score)
                const isModalDragging = modalDragIndex === index
                const isModalDragOver = modalDragOverIndex === index && modalDragIndex !== index

                return (
                  <div
                    key={net.id}
                    draggable
                    onDragStart={(e) => handleModalDragStart(e, index)}
                    onDragEnter={() => handleModalDragEnter(index)}
                    onDragOver={handleModalDragOver}
                    onDrop={(e) => handleModalDrop(e, index)}
                    onDragEnd={handleModalDragEnd}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-[8px] group transition-all border
                      ${isModalDragOver
                        ? 'border-[#535EA4] bg-[#F0F3FF]'
                        : 'border-transparent hover:border-[#E8E8E8] hover:bg-[#FAFAFA]'
                      }
                      ${isModalDragging ? 'opacity-40' : ''}
                    `}
                    title={`${net.name} — Health Score: ${score}/100`}
                  >
                    {/* Position number */}
                    <span className="text-[11px] text-[#C0C0C0] w-5 text-right flex-shrink-0 font-mono">
                      {index + 1}
                    </span>

                    {/* Drag handle */}
                    <GripVertical className="h-4 w-4 text-[#D0D0D0] group-hover:text-[#939393] cursor-grab active:cursor-grabbing flex-shrink-0 transition-colors" />

                    {/* Network name */}
                    <span className="text-[13px] font-medium text-[#444] flex-1 truncate" title={net.name}>
                      {net.name}
                    </span>

                    {/* Progress bar */}
                    <div className={`h-[10px] w-[80px] rounded-[3px] ${colors.bg} relative overflow-hidden flex-shrink-0`}>
                      <div
                        className={`h-full ${colors.bar} rounded-[3px]`}
                        style={{ width: `${score}%` }}
                      />
                    </div>

                    {/* Score badge */}
                    <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-[4px] w-[34px] text-center flex-shrink-0 ${colors.badge}`}>
                      {score}
                    </span>

                    {/* Up / Down buttons */}
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => moveModalItem(index, 'up')}
                        disabled={index === 0}
                        className="p-0.5 rounded text-[#C0C0C0] hover:text-[#535EA4] hover:bg-[#EEF1FF] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                      >
                        <ChevronUp className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => moveModalItem(index, 'down')}
                        disabled={index === modalNetworks.length - 1}
                        className="p-0.5 rounded text-[#C0C0C0] hover:text-[#535EA4] hover:bg-[#EEF1FF] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                      >
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-[#EEE] flex-shrink-0">
              <span className="text-[11px] text-[#B0B0B0]">{modalNetworks.length} networks</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const defaultOrder = sourceNetworks.map((n) => n.id)
                    setModalOrder(defaultOrder)
                  }}
                  className="px-3 py-1.5 text-[12px] text-[#939393] hover:text-[#535EA4] hover:bg-[#F5F5F5] rounded-[6px] transition-colors"
                >
                  Reset
                </button>
                <button
                  onClick={closeReorderModal}
                  className="px-4 py-1.5 text-[12px] font-medium text-white bg-[#535EA4] hover:bg-[#424D93] rounded-[6px] transition-colors"
                >
                  Apply Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
